import { all, call, delay, put, select, takeLeading } from 'redux-saga/effects';

import { REPLICATION_DELAY } from '@app/constants/delayTime';
import GLOBAL_ERROR_MESSAGE from '@app/constants/errorMessages';
import workspaceService from '@app/libs/workspaceService';
import {
  BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_REQUEST,
  BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_REQUEST,
  BULK_REQUEST_REQUEST_TASKS_TASKS__RELOAD_REQUEST,
} from '@app/store/actionTypes';
import {
  fetchRequestTasks,
  fetchRequestTasksFailure,
  fetchRequestTasksSuccess,
  putRequestTasks,
  putRequestTasksFailure,
  putRequestTasksSuccess,
  reloadRequestTasks,
  reloadRequestTasksFailure,
  reloadRequestTasksSuccess,
} from '@app/store/bulkRequest/requestTasks/tasks/actions';
import { getCount, getPage } from '@app/store/bulkRequest/requestTasks/tasks/selectors';
import { modalOpen } from '@app/store/modal/actions';
import {
  getRequestTasks,
  putRequestTasks as putRequestTasksAPI,
  PutRequestTasksResponse,
} from '@app/store/transport-layer/bulk-request/bulk-requests/request-tasks';

export function* fetchRequestTasksSaga({ payload }: ReturnType<typeof fetchRequestTasks>) {
  const { bulkRequestId, ...params } = payload;
  try {
    const tasks: Awaited<ReturnType<typeof getRequestTasks>> = yield call(
      getRequestTasks,
      workspaceService.getWorkspaceId(),
      bulkRequestId,
      params
    );
    yield put(fetchRequestTasksSuccess(tasks));
  } catch (error) {
    yield put(fetchRequestTasksFailure(error));
  }
}

export function* reloadRequestTasksSaga({ payload }: ReturnType<typeof reloadRequestTasks>) {
  try {
    const count: ReturnType<typeof getCount> = yield select(getCount);
    const page: ReturnType<typeof getPage> = yield select(getPage);
    const tasks: Awaited<ReturnType<typeof getRequestTasks>> = yield call(
      getRequestTasks,
      workspaceService.getWorkspaceId(),
      payload,
      {
        page: 1,
        count: (page ?? 1) * (count ?? 10),
      }
    );
    yield put(reloadRequestTasksSuccess(tasks));
  } catch (error) {
    yield put(reloadRequestTasksFailure(error));
  }
}

export function* putRequestTasksSaga({
  payload: { bulkRequestId, formValues },
}: ReturnType<typeof putRequestTasks>) {
  try {
    const { bulkRequestId: returnedBulkRequestId }: PutRequestTasksResponse = yield call(
      putRequestTasksAPI,
      workspaceService.getWorkspaceId(),
      bulkRequestId,
      {
        formValues,
      }
    );
    yield put(putRequestTasksSuccess(returnedBulkRequestId));

    yield delay(REPLICATION_DELAY);

    yield put(fetchRequestTasks({ bulkRequestId, page: 1, count: 100 }));
  } catch (error) {
    yield put(putRequestTasksFailure(error));
    yield put(
      modalOpen('CONFIRM', {
        title: '대량전송 엑셀 업로드 실패',
        content: GLOBAL_ERROR_MESSAGE[error.message] || error.message,
      })
    );
  }
}

export default function* sagas() {
  yield all([
    takeLeading(BULK_REQUEST_REQUEST_TASKS_TASKS__FETCH_REQUEST, fetchRequestTasksSaga),
    takeLeading(BULK_REQUEST_REQUEST_TASKS_TASKS__PUT_REQUEST, putRequestTasksSaga),
    takeLeading(BULK_REQUEST_REQUEST_TASKS_TASKS__RELOAD_REQUEST, reloadRequestTasksSaga),
  ]);
}
